import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { CheckCircle, XCircle } from "lucide-react";

const OIDCCallback = () => {
  const { translate } = useLanguage();
  const { handleCallback } = useAuth();
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const processCallback = async () => {
      if (hasProcessed.current) return;
      hasProcessed.current = true;

      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        const state = urlParams.get("state");

        if (!code) {
          throw new Error("No authorization code received");
        }

        await handleCallback(code, state);
        setStatus("success");

        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      } catch (err) {
        console.error("Callback processing error:", err);

        if (err.message.includes("role") || err.message.includes("403")) {
          setError(
            "Access Denied: You don't have the required permissions. Please contact your administrator to assign you the appropriate role (admin or user)."
          );
        } else if (
          err.message.includes("500") ||
          err.response?.status === 500
        ) {
          setError(
            "Server Error: There was a problem with the authentication server. Please try again in a few moments."
          );
        } else if (err.message.includes("No authorization code")) {
          setError(
            "Authentication Error: No authorization code was received. Please try logging in again from the main page."
          );
        } else if (err.message.includes("Failed to complete login")) {
          setError(
            "Login Failed: Unable to complete the authentication process. Please try again."
          );
        } else {
          setError(err.message);
        }

        setStatus("error");
      }
    };

    processCallback();
  }, [handleCallback]);

  if (status === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--color-bg)" }}
      >
        <div className="text-center">
          <div
            className="w-10 h-10 rounded-full border-2 border-t-transparent mx-auto mb-4"
            style={{
              borderColor: "var(--color-mint)",
              borderTopColor: "transparent",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: "var(--color-text-primary)" }}
          >
            {translate("processingLogin") || "Processing Login..."}
          </h2>
          <p style={{ color: "var(--color-text-secondary)" }}>
            {translate("pleaseWait") ||
              "Please wait while we complete your login..."}
          </p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--color-bg)" }}
      >
        <div className="text-center">
          <CheckCircle
            className="w-12 h-12 mx-auto mb-4"
            style={{ color: "var(--color-success)" }}
          />
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: "var(--color-text-primary)" }}
          >
            {translate("loginSuccessful") || "Login Successful!"}
          </h2>
          <p style={{ color: "var(--color-text-secondary)" }}>
            {translate("redirectingToApp") ||
              "Redirecting to the application..."}
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--color-bg)" }}
      >
        <div className="text-center max-w-md">
          <XCircle
            className="w-12 h-12 mx-auto mb-4"
            style={{ color: "var(--color-error)" }}
          />
          <h2
            className="text-xl font-semibold mb-2"
            style={{ color: "var(--color-text-primary)" }}
          >
            {translate("loginFailed") || "Login Failed"}
          </h2>
          <p className="mb-4" style={{ color: "var(--color-text-secondary)" }}>
            {error ||
              translate("loginError") ||
              "An error occurred during login"}
          </p>
          <button
            onClick={() => (window.location.href = "/")}
            className="px-4 py-2 text-white rounded-lg transition-colors"
            style={{
              backgroundColor: "var(--color-mint)",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--color-mint-dark)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--color-mint)")
            }
          >
            {translate("tryAgain") || "Try Again"}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default OIDCCallback;
